import React, { useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve relative paths (e.g. /api/uploads/foo.mp4) to absolute URLs so the
// browser can fetch them through the K8s ingress.
const absolutize = (url) => {
  if (typeof url !== 'string') return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

// Returns { kind, src } for a given URL.
//   kind: 'video' | 'youtube' | 'vimeo' | 'image'
const detectMedia = (url) => {
  if (typeof url !== 'string') return { kind: 'image', src: url };
  const u = url.trim();
  const lower = u.toLowerCase();

  // Direct video files
  if (lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.m4v')) {
    return { kind: 'video', src: absolutize(u) };
  }

  // YouTube: https://youtu.be/ID, https://www.youtube.com/watch?v=ID, /shorts/ID, /embed/ID
  const yt =
    u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/i);
  if (yt) {
    return { kind: 'youtube', src: `https://www.youtube.com/embed/${yt[1]}` };
  }

  // Vimeo: https://vimeo.com/123456789, https://player.vimeo.com/video/123456789
  const vm = u.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/i);
  if (vm) {
    return { kind: 'vimeo', src: `https://player.vimeo.com/video/${vm[1]}` };
  }

  return { kind: 'image', src: absolutize(u) };
};

const Slide = ({ item, alt, index }) => {
  const { kind, src } = detectMedia(item);

  if (kind === 'video') {
    return <video src={src} className="w-full h-full object-cover" controls playsInline />;
  }
  if (kind === 'youtube' || kind === 'vimeo') {
    return (
      <iframe
        src={src}
        title={`${alt || 'media'} ${index + 1}`}
        className="w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
      />
    );
  }
  return (
    <img
      src={src}
      alt={`${alt || 'media'} - ${index + 1}`}
      className="w-full h-full object-cover"
    />
  );
};

const MediaCarousel = ({ media, alt, aspectClassName = 'aspect-[4/3]' }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Single item or legacy string
  if (!Array.isArray(media) || media.length <= 1) {
    const single = Array.isArray(media) ? media[0] : media;
    if (!single) return null;
    return (
      <div className={`${aspectClassName} overflow-hidden rounded-sm border border-maroon/10 bg-sand/20`}>
        <Slide item={single} alt={alt} index={0} />
      </div>
    );
  }

  return (
    <div className={`relative ${aspectClassName} overflow-hidden rounded-sm border border-maroon/10 bg-sand/20`}>
      <div className="embla h-full" ref={emblaRef}>
        <div className="embla__container flex h-full">
          {media.map((item, index) => (
            <div className="embla__slide flex-[0_0_100%] min-w-0" key={index}>
              <Slide item={item} alt={alt} index={index} />
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollPrev(); }}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-maroon p-2 rounded-full shadow-lg transition-all z-10"
        aria-label="Previous"
        data-testid="carousel-prev"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollNext(); }}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-maroon p-2 rounded-full shadow-lg transition-all z-10"
        aria-label="Next"
        data-testid="carousel-next"
      >
        <ChevronRight size={20} />
      </button>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
        {media.map((_, index) => (
          <div key={index} className="w-2 h-2 rounded-full bg-white/70" />
        ))}
      </div>
    </div>
  );
};

export default MediaCarousel;
